import { Languages, MessageCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { usePublic } from '@/contexts/PublicContext';
import type { FeedbackPeakAnalysisOptions } from '@/features/Feedback/components/FeedbackListModal/peak-analysis';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import service from '@/services';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';

import type { WordCloudBasis } from '../types';

type OpenWordCloudFeedbackListModal = {
  keyword: string;
  filter: StructuredFilter;
  translatedKeyword?: string;
  basis?: WordCloudBasis;
  peakAnalysis?: FeedbackPeakAnalysisOptions;
};

const useWordCloudFeedbackListModal = () => {
  const { workspace_id } = useParams();
  const isPublic = usePublic();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);

  const openWordCloudFeedbackListModal = ({
    keyword,
    filter,
    translatedKeyword,
    basis,
    peakAnalysis,
  }: OpenWordCloudFeedbackListModal) => {
    if (!workspace_id) throw new Error('workspace_id is required');

    // 번역본이 존재하면서 keyword(원문)과 다르면 번역본이라고 판단
    const isTranslated = translatedKeyword && translatedKeyword !== keyword;

    setNewModal<FeedbackBlockListModalOptions>({
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      alias: keyword,
      idx: 0,
      options: {
        queryKeys: ['feedback', 'list', keyword],
        title: 'Feedback List',
        peakAnalysis,
        subTitle: (
          <div className="space-y-1">
            <div className="flex gap-1 items-center text-sm font-medium">
              <MessageCircle className="size-4" />
              {keyword}
            </div>
            {isTranslated ? (
              <div className="flex gap-1 items-center text-sm font-medium">
                <Languages className="size-4" />
                {translatedKeyword}
              </div>
            ) : (
              <></>
            )}
          </div>
        ),
        domain: 'feedback-blocks',
        fetcher: async (sortStatus, paginate_token) => {
          const res = await service.wordCloud.fetchWordCloudFeedback(
            workspace_id,
            sortStatus,
            {
              filter,
              keyword: keyword,
              paginate_token: paginate_token || undefined,
              basis,
            },
            isPublic,
          );
          return {
            count: res.count,
            list: res.feedbacks,
            paginate_token:
              res.feedbacks[res.feedbacks.length - 1]?.paginate_token,
          };
        },
      },
    });
  };

  return openWordCloudFeedbackListModal;
};

export default useWordCloudFeedbackListModal;
